import { Component, Injector, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, concatMap, debounceTime, distinctUntilChanged, mergeMap, Observable, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AbstractComponent, AttendanceService, Channel, ChannelService, CheckerContactTypeEnum, ConfirmationComponent, Contact, ContactEditComponent, ContactService, ContactUploadCsvComponent, ExportService, Pager, Tag, TagService, UtilHelper } from 'lib-trend-core';
import { ContactCustomerPortfolioTransferComponent } from '../contact-customer-portfolio-transfer/contact-customer-portfolio-transfer.component';

@Component({
    selector: 'contact-list-component',
    templateUrl: './contact-list.v2.component.html',
    // templateUrl: './contact-list.component.html',
    styleUrls: ['./contact-list.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactListComponent extends AbstractComponent {
  pager: Pager<Contact> = new Pager<Contact>({ perPage: 12 });
  @Input() toggleTableCard: boolean = false;
  @Input() showAddContactButton: boolean = true;
  @Input() showImportContactButton: boolean = true;
  @Input() showActionCustomerPortfolioButtons: boolean = false;
  @Input() showCards: boolean = true;
  @Input() useSavedView: boolean = true;
  @Input() idUserCustomerPortfolio: string;

  filterControlTags: FormControl = new FormControl('');
  filterControlChannel: FormControl = new FormControl('');

  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  selectedTagsId: Array<string> | null = new Array<string>();
  private selectedChannelId: string | null = null;
  currentUser: any

  listObservable: Observable<Pager<Contact>>;
  private termoDaBusca: Subject<string> = new Subject<string>();

  listTag: Array<Tag> = new Array<Tag>();
  allTags: Array<Tag> = new Array<Tag>();
  private totalAttendances!: Array<{ _id: string, count: number, channel: Channel }>;

  listChannel: Array<Channel> = new Array<Channel>();
  allChannel: Array<Channel> = new Array<Channel>();
  labelChannel: string = 'contact.list.channelFilter';
  labelBlocked: string = 'contact.contactList.blockedFilter';

  isPicker: boolean = false;

  totalContactValidated: number;
  totalContactNotValidated: number;
  totalContactInvalid: number;
  totalContact: number

  contactInfoChart: { validated: number, notValidated: number, invalid: number, total: number };

  radius: number;
  isFilter: string = '';

  selectedContactsIds = new Array<string>();

  selectAll: boolean = false;

  selectedCard: string = '';

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public contactService: ContactService,
    public attendanceService: AttendanceService,
    private tagService: TagService,
    private channelService: ChannelService,
    public exportService: ExportService
  ) {
    super(injector);
    this.createForm();
    this.loadingContent = true;
  }

  ngOnInit(): void {
    this.currentUser = this.getCurrentUserUser();
    this.getListChannel();
    this.fetchFilterTags();
    this.fetchFilterChannel();
    this.setupCombinedFiltersAndSearch();
    const savedView = localStorage.getItem('tableCardView');
    this.toggleTableCard = this.useSavedView && savedView ? JSON.parse(savedView) : false;
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      type: [null],
      dateRange: this.formBuilder.group({
        dateStart: [null],
        dateEnd: [null],
      }),
      channel: [null],
      tags: [[]],
      valid: [null],
      blocked: [null]
    });
  }

  setupObservableSearch() {
    this.listObservable = this.termoDaBusca
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(switchMap(term => {
        return this.contactService.getAll(this.pager.page, this.pager.perPage, term, this.searchParams);
      }));
    this.listObservable.subscribe((pager: Pager<Contact>) => {
      this.pager = pager;
    });
  }

  private setupCombinedFiltersAndSearch(): void {
    combineLatest([
      this.formGroup.valueChanges.pipe(
        startWith(this.formGroup.value),
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      ),
      this.termoDaBusca.pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged()
      )
    ])
      .pipe(
        tap(() => {
          this.loadingContent = true;
          this.pager.list = [];
        })
      )
      .subscribe(([formValues, searchTerm]) => {
        this.searchString = searchTerm;
        this.getList();
      });
  }

  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo;
    this.loadingContent = true;
    this.pager.list = [];
    this.termoDaBusca.next(termo);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.loadingContent = true;
    this.pager.list = [];
    this.getList();
  }

  private getList() {
    this.loadingContent = true;
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    const type = this.formGroup.get('type').value;
    if (type) {
      this.searchParams['type'] = type;
    }

    if (this.idUserCustomerPortfolio) {
      this.searchParams['customerPortfolioUser'] = this.idUserCustomerPortfolio;
    }

    const tags = this.formGroup.get('tags')?.value;
    if (!!tags && tags.length > 0) {
      this.searchParams['tags'] = tags;
    }

    const channel = this.formGroup.get('channel')?.value
    if (channel) {
      this.searchParams['channel'] = channel;
    }

    const dateRange = this.formGroup.get('dateRange').value;
    if (dateRange?.dateStart && dateRange?.dateEnd) {
      this.searchParams['dateStart'] = dateRange.dateStart.getTime();
      this.searchParams['dateEnd'] = dateRange.dateEnd.getTime();
    }

    const valid = this.formGroup.get('valid')?.value
    if (valid) {
      this.searchParams['valid'] = valid;
    }

    // 'true' | 'false' | null. Null means "filter normally": blocked contacts included.
    const blocked = this.formGroup.get('blocked')?.value;
    if (blocked === 'true' || blocked === 'false') {
      this.searchParams['blocked'] = blocked;
    }

    const getValueTotalizer = (totalizer: Array<{ count: number, validStatus: string }>, checkerContactTypeEnum: CheckerContactTypeEnum) => {
      let result = 0;
      const filtered = totalizer.filter((total) => total.validStatus === checkerContactTypeEnum.toString());
      if (filtered.length > 0) {
        const mapped = filtered.map((total) => total.count);
        if (!!mapped && mapped.length > 0) {
          result = mapped.reduce((result) => result);
        }
      }
      return result;
    }

    this.contactService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(
        mergeMap((pager: Pager<Contact>) => {
          this.pager = pager;
          const totalizerParams: any = { ...this.searchParams };
          if (this.searchString) {
            totalizerParams.search = this.searchString;
          }
          return this.contactService.getTotalizerContact(totalizerParams);
        }),
        mergeMap((totalizer: Array<{ count: number, validStatus: string }>) => {
          this.totalContactValidated = getValueTotalizer(totalizer, CheckerContactTypeEnum.VALIDATED);
          this.totalContactNotValidated = getValueTotalizer(totalizer, CheckerContactTypeEnum.NOT_VALIDATED);
          this.totalContactInvalid = getValueTotalizer(totalizer, CheckerContactTypeEnum.INVALID);

          this.totalContact = this.totalContactValidated + this.totalContactNotValidated + this.totalContactInvalid

          this.contactInfoChart = {
            validated: (this.totalContactValidated / this.totalContact) * 100,
            notValidated: (this.totalContactNotValidated / this.totalContact) * 100,
            invalid: (this.totalContactInvalid / this.totalContact) * 100,
            total: (this.totalContact / this.totalContact) * 100
          }

          this.radius = Math.max(50, Math.floor(this.totalContact / 20));

          return this.tagService.getList();
        }),
        mergeMap((tags: Array<Tag>) => {
          this.listTag = tags;
          this.allTags = tags;
          const idsContacts = this.pager.list.map(contact => contact._id);
          return this.attendanceService.getCountAttendancesByContacts(idsContacts);
        }),
      ).subscribe((counts: any) => {
        this.totalAttendances = counts;
        this.loadingContent = false;
      });
  }

  add(): void {
    const dialogRef = this.dialog.open(ContactEditComponent, {
      width: '800px',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  import(): void {
    const dialogRef = this.dialog.open(ContactUploadCsvComponent, {
      width: '58vw',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  edit(item: Contact): void {
    const dialogRef = this.dialog.open(ContactEditComponent, {
      width: '700px',
      data: item
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  view(item: Contact): void {
    this.router.navigate(['/contact/view', item._id]);
  }

  delete(item: Contact): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.contactService.delete(item._id).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(this.t('contact.contactList.deleteSuccess'));
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  getTagTitle(item: Contact): { display: string; tooltip: string } {
    const tags = item.tags.map(tag => tag.title);
    const displayedTags = tags.slice(0, 2);
    const remainingCount = tags.length - 2;

    const display = displayedTags
      .map((tag, index) => `${tag.slice(0, 15)}${index < displayedTags.length - 1 ? ', ' : ''}`)
      .join('') + (remainingCount > 0 ? ` +${remainingCount}` : '');

    if (display.length === 0) {
      return { display: '-', tooltip: '' };
    }

    const tooltip = tags.join(', ');

    return { display, tooltip };
  }

  getAddress(item: Contact): string {
    return UtilHelper.getContactAddress(item);
  }

  getChannelContact(contactId: string): string {
    if (this.totalAttendances && this.totalAttendances.length > 0) {
      const channel = this.totalAttendances
        .filter((value: { _id: string, count: number, channel: Channel }) => value && value._id === contactId)
        .map((value: { _id: string, count: number, channel: Channel }) => value.channel)
        .reduce((channelAcc, channelCurrent) => channelCurrent || channelAcc, null);

      return (channel && channel.name) && (channel.removed !== true) ? channel.name : '-';
    }

    return '-';
  }

  getTagsContact(contact: Contact): string {
    const tagTitle = contact.tags.map(tag => tag.title).join(', ');

    return tagTitle.length === 0 ? '-' : tagTitle;
  }

  getCountAttendades(contactId: string): string {
    if (this.totalAttendances) {
      const total = this.totalAttendances
        .filter((value) => value && value._id === contactId)
        .map((value) => value.count)
        .toString();

      return total.length === 0 ? this.t('contact.list.noAttendance') : total;
    }

    return '-';
  }

  selectTag(event: Event, id: string): void {
    const inputElement = event.target as HTMLInputElement;
    const tagsControl = this.formGroup.get('tags');
    const currentTags = tagsControl?.value || [];

    if (inputElement.checked) {
      tagsControl?.setValue([...currentTags, id]);
    } else {
      tagsControl?.setValue(currentTags.filter((tagId: string) => tagId !== id));
    }
  }

  searchByChannel(channel: Channel): void {
    if (channel) {
      const firstWord = channel.name.split(' ')[0].slice(0, 6);
      this.labelChannel = firstWord;
      this.formGroup.get('channel').setValue(channel._id);
    } else {
      this.labelChannel = 'contact.list.channelFilter';
      this.formGroup.get('channel').setValue(null);
    }
  }

  /**
   * Blocking is per channel (`blockedChannels` holds channel ids), but this screen has no
   * channel context, so "blocked" here means blocked on at least one channel. `null` keeps
   * the normal listing, blocked contacts included.
   */
  searchByBlocked(value: 'true' | 'false' | null): void {
    if (value === 'true') {
      this.labelBlocked = 'contact.contactList.blockedOnly';
    } else if (value === 'false') {
      this.labelBlocked = 'contact.contactList.notBlockedOnly';
    } else {
      this.labelBlocked = 'contact.contactList.blockedFilter';
    }
    this.formGroup.get('blocked').setValue(value);
  }

  isBlocked(contact: Contact): boolean {
    return (contact?.blockedChannels?.length ?? 0) > 0;
  }

  getBlockedChannelsTooltip(contact: Contact): string {
    const names = (contact?.blockedChannels ?? [])
      .map((idChannel: string) => this.allChannel.find((channel: Channel) => channel._id === idChannel)?.name)
      .filter((name: string) => !!name);
    return names.length > 0
      ? this.t('contact.contactList.blockedIn', { channels: names.join(', ') })
      : this.t('contact.contactList.blockedBadge');
  }

  private getListChannel(): void {
    this.channelService.getList().subscribe({
      next: (list: Array<Channel>) => {
        this.allChannel = list;
        this.listChannel = list;
      },
    });
  }

  onPeriodChange(event: any): void {
    const today = new Date();
    switch (event.value) {
      case '1':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(),
          dateEnd: new Date()
        });
        this.picker.close();
        break;
      case '7':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 7)),
          dateEnd: new Date()
        });
        this.picker.close();
        break;
      case '15':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 15)),
          dateEnd: new Date()
        });
        this.picker.close();
        break;
      case '30':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 30)),
          dateEnd: new Date()
        });
        this.picker.close();
        break;
      case '60':
        this.formGroup.get('dateRange').patchValue({
          dateStart: new Date(today.setDate(today.getDate() - 60)),
          dateEnd: new Date()
        });
        this.picker.close();
        break;
    }
  }

  private fetchFilterTags(): void {
    this.filterControlTags.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((value: string) => {
      const currentValue = this.selectedTagsId || [];

      this.allTags = this.listTag.filter(tag =>
        tag.title.toLowerCase().includes(value.toLowerCase())
      );

      currentValue.forEach(tagId => {
        const existingTag = this.listTag.find(tag => tag._id === tagId);
        if (existingTag && !this.allTags.includes(existingTag)) {
          this.allTags.push(existingTag);
        }
      });
    });
  }

  onFocusTags(): void {
    this.allTags = this.listTag;
  }

  private fetchFilterChannel(): void {
    this.filterControlChannel.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.selectedChannelId || null;

      this.listChannel = this.allChannel.filter(channel =>
        channel.name.toLowerCase().includes(value.toLowerCase()) &&
        channel._id !== currentValue
      );

      const existingChannel = this.allChannel.find(channel => channel._id === currentValue);
      if (existingChannel) {
        this.listChannel.push(existingChannel);
      }
    });
  }

  onFocusChannel(): void {
    this.listChannel = this.allChannel;
  }

  searchByValid(validType: string): void {
    this.selectedCard = validType;
    this.isFilter = validType
    this.formGroup.get('valid').setValue(validType || null);
  }

  clearSearchByValid() {
    this.selectedCard = '';
    this.isFilter = undefined;
    this.formGroup.get('valid').setValue(null);
  }

  getContactStatus(status: string | undefined): string {
    switch (status) {
      case 'VALIDATED':
        return this.t('contact.valid.validated');
      case 'NOT_VALIDATED':
        return this.t('contact.valid.notValidated');
      case 'INVALID':
        return this.t('contact.valid.invalid');
      default:
        return this.t('contact.valid.unknown');
    }
  }

  toggleTableCards() {
    this.toggleTableCard = !this.toggleTableCard;
    localStorage.setItem('tableCardView', JSON.stringify(this.toggleTableCard));
  }

  /**
   * Manual lead markings (SPEC-2026-08-crm-pipeline-deals-radar — RF-37).
   *
   * They live here, on the contacts list, for three reasons: the flags belong to
   * the CONTACT and not to a deal — setting one from a deal card would silently
   * affect every other deal of that lead; this row menu already carries
   * commercial judgement (the portfolio actions), which is the same family; and
   * out-of-profile triage is done by sweeping a list, not one lead at a time.
   *
   * Deliberately NOT in the attendance chat header, where "block contact" already
   * lives: that one blocks a channel and is an attendance action. These two are
   * commercial, and the out-of-profile endpoint refuses attendants outright.
   */

  /**
   * The lead markings and their handlers moved to the CUSTOMERS list on 2026-08-21
   * (`kanban/customer`): marking a lead is enrichment, and enrichment belongs to the CRM
   * record of the lead, never to its messaging identity.
   */

  removeFromCustomerPortfolio(contact: Contact): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.contactService.removeFromCustomerPortfolio([contact._id]).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(this.t('contact.contactList.removeSuccess'));
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  removeFromCustomerPortfolioList(): void {
    if (this.selectedContactsIds.length === 0) {
      this.alertService.error(this.t('contact.contactList.selectContactToRemove'));
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.contactService.removeFromCustomerPortfolio(this.selectedContactsIds).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(this.t('contact.contactList.removeListSuccess'));
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  transferCustomerPortfolio(contact: Contact): void {
    const dialogRef = this.dialog.open(ContactCustomerPortfolioTransferComponent, {
      width: '700px',
      data: [contact._id]
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  transferCustomerPortfolioList(): void {
    if (this.selectedContactsIds.length === 0) {
      this.alertService.error(this.t('contact.contactList.selectContactToTransfer'));
      return;
    }
    const dialogRef = this.dialog.open(ContactCustomerPortfolioTransferComponent, {
      width: '700px',
      data: this.selectedContactsIds
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getList();
    });
  }

  changeSelectContact(id: string): void {
    const index = this.selectedContactsIds.indexOf(id);
    if (index === -1) {
      this.selectedContactsIds.push(id);
    } else {
      this.selectedContactsIds.splice(index, 1);
    }
  }

  selectDeselectAll(): void {
    this.selectedContactsIds = [];
    if (this.selectAll) {
      this.selectedContactsIds = this.pager.list.map(contact => contact._id);
    }
  }

  exportContacts(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('contact.contactList.exportConfirm')
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.loading = true;
        this.fetchContactsRecursively([], new Pager<Contact>({ perPage: 10000 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (contacts: Array<Contact>) => {
              if (!contacts || contacts.length === 0) {
                this.alertService.error(this.t('contact.contactList.noContactToExport'));
                this.loading = false;
                return;
              }

              const formattedData = this.formatContactData(contacts);
              this.exportService
                .generateExcelContactReport(formattedData)
                .then((buffer: any) => this.downloadExcelFile(buffer))
                .catch((err: any) => {
                  const errorMessage = err?.error?.message || err?.message || this.t('contact.contactList.excelReportError');
                  this.alertService.error(errorMessage);
                })
                .finally(() => {
                  this.alertService.success(this.t('contact.contactList.excelReportSuccess'));
                  this.loading = false;
                });
            },
            error: (err: any) => {
              const errorMessage = err?.error?.message || err?.message || this.t('contact.contactList.fetchContactsError');
              this.alertService.error(errorMessage);
              this.loading = false;
            }
          });
      }
    });
  }

  private fetchContactsRecursively(contacts: Contact[], pager: Pager<Contact>): Observable<Array<Contact>> {

    return this.contactService.getAll(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap(newPager => {
          const allContacts = [...contacts, ...newPager.list];
          if (allContacts.length >= newPager.total) {
            return new Observable<Array<Contact>>(observer => {
              observer.next(allContacts);
            });
          } else {
            return this.fetchContactsRecursively(
              allContacts,
              { ...pager, page: pager.page + 1 }
            );
          }
        })
      );
  }

  private formatContactData(contacts: Contact[]): any[] {
    return contacts.map((contact: Contact) => {
      const channel = this.getChannelContact(contact._id);
      const tags = this.getTagsContact(contact);
      const totalAttendances = this.getCountAttendades(contact._id);
      const address = this.getAddress(contact);

      return {
        name: contact.name || '-',
        phone: contact.phone || '-',
        email: contact.email || '-',
        channel: { name: channel || '-' },
        status: this.getContactStatus(contact.valid || 'NOT_VALIDATED'),
        createdAt: contact.createdAt ? new Date(contact.createdAt) : new Date(),
        tags: tags || '-',
        address: address || '-',
        origin: contact.leadSource?.name || '-',
        totalAttendances: totalAttendances || '0'
      };
    });
  }

  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toLocaleDateString().replace(/\//g, '-');
    a.download = `Contatos_${date}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
